import {useMemo, useState} from 'react';
import {
  Boxes,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  RotateCcw,
  ScanBarcode,
} from 'lucide-react';
import {Card, CardContent} from '@/components/ui/Card';
import {Button} from '@/components/ui/Button';
import {Input} from '@/components/ui/Input';
import {Badge} from '@/components/ui/Badge';
import {Skeleton} from '@/components/ui/Skeleton';
import {OutcomePill} from '@/components/app/OutcomePill';
import {
  describeDownloadError,
  openDownload,
  resendIntegration,
  toCalendarDay,
  transmissionIntegration,
  useTransmissionDownload,
  useTransmissionLogs,
} from '@/lib/api/transmissions';
import {ResendTransmissionDialog} from './ResendTransmissionDialog';
import {notifyApiError} from '@/lib/notifications/apiErrorBus';
import {formatDate, formatDateTime, formatNumber} from '@/lib/utils/format';
import {cn} from '@/lib/utils/cn';
import {useCurrentProvider} from '@/lib/providers/useCurrentProvider';
import type {
  TransmissionKind,
  TransmissionLog,
  TransmissionOutcome,
} from '@/lib/api/types';

// The two streams are separate tables behind separate endpoints, so they can't
// be interleaved into one list — they're tabs rather than a filter.
const KIND_TABS: Array<{
  id: TransmissionKind;
  label: string;
  icon: typeof Boxes;
}> = [
  {id: 'pos', label: 'POS reports', icon: ScanBarcode},
  {id: 'inventory', label: 'Inventory reports', icon: Boxes},
];

// Chips are built on `outcome`, the normalised value — the raw `status` column
// holds three different backend vocabularies and isn't safe to filter on.
const OUTCOME_FILTERS: Array<{
  value: TransmissionOutcome | 'all';
  label: string;
}> = [
  {value: 'all', label: 'All'},
  {value: 'sent', label: 'Sent'},
  {value: 'failed', label: 'Failed'},
  {value: 'pending', label: 'Pending'},
];

const PAGE_SIZE = 25;

/** `batchIndex` can legitimately be 0, so it needs a null check, not a falsy one. */
function formatBatch(row: TransmissionLog): string {
  return row.batchIndex == null ? '—' : `#${row.batchIndex}`;
}

/** The window of documents a POS run reported on. Inventory rows have none. */
function formatWindow(row: TransmissionLog): string {
  const {startDate, endDate} = row;
  if (!startDate && !endDate) return '—';
  if (startDate && endDate) {
    return startDate === endDate
      ? formatDate(startDate)
      : `${formatDate(startDate)} – ${formatDate(endDate)}`;
  }
  return formatDate((startDate ?? endDate) as string);
}

/**
 * The window a POS row reported on, as the calendar days a replay would take.
 *
 * Undefined when the row recorded no window at all — an older run, or a row
 * from a flow that never wrote one. That's what disables the per-row replay,
 * rather than guessing a range the run never covered.
 */
function rowWindow(
  row: TransmissionLog
): {from: string; to: string} | undefined {
  const from = toCalendarDay(row.startDate);
  const to = toCalendarDay(row.endDate);
  // A half-open window is a single day, not an unbounded one: the run reported
  // on the day it did record, and the other bound was simply never written.
  if (!from) return to ? {from: to, to} : undefined;
  return {from, to: to ?? from};
}

/**
 * Replay from this row.
 *
 * On POS the common repair is "that run failed, send the same days again", so
 * the range comes from the row rather than being retyped out of the table into
 * the dialog. Disabled — rather than hidden — on a row with no recorded
 * window, so the column stays aligned and the tooltip can say why.
 *
 * An inventory row carries no window and passes none: the replay re-reads the
 * stock table as it stands. The row is the entry point, not the argument, and
 * the label says so — clicking it on a week-old row does not re-send what that
 * row sent.
 */
function ResendRowButton({
  row,
  kind,
  onSelect,
}: {
  row: TransmissionLog;
  kind: TransmissionKind;
  onSelect: (range: {from?: string; to?: string}) => void;
}) {
  if (kind === 'inventory') {
    const label = 'Resend the stock table as it stands now';
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onSelect({})}
        aria-label={label}
        title={label}
      >
        <RotateCcw className="h-4 w-4" />
      </Button>
    );
  }

  const replayWindow = rowWindow(row);
  const label = replayWindow
    ? `Resend the ${formatWindow(row)} window`
    : 'This run recorded no window to replay';
  return (
    <Button
      variant="ghost"
      size="icon"
      disabled={!replayWindow}
      onClick={() => replayWindow && onSelect(replayWindow)}
      aria-label={label}
      title={label}
    >
      <RotateCcw className="h-4 w-4" />
    </Button>
  );
}

export function TransmissionsPage() {
  const provider = useCurrentProvider();
  const [kind, setKind] = useState<TransmissionKind>('pos');
  const [outcome, setOutcome] = useState<TransmissionOutcome | 'all'>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const integration = transmissionIntegration(provider.id, kind);

  // DynamoDB paging is cursor-based and forward-only, so the cursor for every
  // page visited is kept and Prev steps back through them rather than
  // re-walking from page 1. cursors[0] is always undefined (page 1 sends no
  // exclusiveStartKey).
  //
  // The stack is tied to a signature of the query that produced it: a cursor
  // encodes the integration's partition key, so carrying one across a tab or
  // filter change would page a different dataset entirely. A mismatch reads as
  // page 1, which — unlike resetting from an effect — leaves no render in
  // which the old cursor is still live.
  const signature = [provider.id, kind, outcome, dateFrom, dateTo].join('|');
  const [paging, setPaging] = useState<{
    signature: string;
    cursors: Array<string | undefined>;
    index: number;
  }>({signature, cursors: [undefined], index: 0});
  const active = useMemo(
    () =>
      paging.signature === signature
        ? paging
        : {
            signature,
            cursors: [undefined] as Array<string | undefined>,
            index: 0,
          },
    [paging, signature]
  );

  const query = useMemo(
    () => ({
      kind,
      integration: integration ?? '',
      outcome: outcome === 'all' ? undefined : outcome,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      limit: PAGE_SIZE,
      cursor: active.cursors[active.index],
    }),
    [kind, integration, outcome, dateFrom, dateTo, active]
  );

  const {data, isLoading, isError} = useTransmissionLogs(query);
  const rows = data?.items ?? [];

  const download = useTransmissionDownload();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // The flag for every resend affordance on the page. Non-null means "this tab
  // can resend", and its value is the closed-enum integration the endpoint
  // takes — both streams replay, under different slugs.
  const resendable = resendIntegration(provider.id, kind);
  // Null is closed; an object is open, carrying the range to prefill. Holding
  // both in one value means a row click can set the window and open the dialog
  // without a render in between where the window belongs to the previous row.
  const [resendRange, setResendRange] = useState<{
    from?: string;
    to?: string;
  } | null>(null);

  const requestDownload = (row: TransmissionLog) => {
    setDownloadingId(row.id);
    download.mutate(
      {kind, id: row.id},
      {
        onSuccess: doc => openDownload(doc.url),
        // 409 (nothing archived) and 410 (aged out) are ordinary outcomes here,
        // not bugs, so they surface as a message rather than a broken link.
        onError: error =>
          notifyApiError({
            ...describeDownloadError(error),
            url: `/api/${kind === 'pos' ? 'pos' : 'inventory'}-report-logs/downloads`,
          }),
        onSettled: () => setDownloadingId(null),
      }
    );
  };

  const isPos = kind === 'pos';
  const columnCount = isPos ? 9 : 7;
  const hasFilters = outcome !== 'all' || Boolean(dateFrom) || Boolean(dateTo);
  const invertedRange = Boolean(dateFrom && dateTo && dateFrom > dateTo);

  const clearFilters = () => {
    setOutcome('all');
    setDateFrom('');
    setDateTo('');
  };

  if (!integration) {
    return (
      <Card className="glass">
        <CardContent className="py-20 text-center text-sm text-muted-foreground">
          {provider.label} has no transmission log — it pushes documents rather
          than scheduled reports.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Transmissions
          </h1>
          <p className="text-sm text-muted-foreground">
            What {provider.label} was sent, and whether it landed. One row per
            transmitted batch.
          </p>
          {/* Answers "why is there no date range on this tab?" — the resend
              is here, it just takes no window. */}
          {!isPos && (
            <p className="mt-1 text-xs text-muted-foreground">
              Inventory reports transmit the current stock table, so a resend
              has no window to replay — it sends stock as it stands now.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {resendable && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setResendRange({})}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Resend…
            </Button>
          )}
          <Badge variant="muted" className="font-mono">
            {integration}
          </Badge>
        </div>
      </div>

      {/* Stream tabs — POS and inventory are different tables, not one filtered list */}
      <div className="flex items-center gap-1 border-b">
        {KIND_TABS.map(({id, label, icon: Icon}) => (
          <button
            key={id}
            onClick={() => {
              setKind(id);
              setResendRange(null);
            }}
            className={cn(
              'flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              kind === id
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div
          role="radiogroup"
          aria-label="Filter by outcome"
          className="inline-flex items-center rounded-md border bg-muted/30 p-0.5"
        >
          {OUTCOME_FILTERS.map(o => (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={outcome === o.value}
              onClick={() => setOutcome(o.value)}
              className={cn(
                'rounded-[5px] px-3 py-1.5 text-xs font-medium transition-colors',
                outcome === o.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {o.label}
            </button>
          ))}
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">
            Sent from
          </span>
          <Input
            type="date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={e => setDateFrom(e.target.value)}
            className="w-[160px]"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">
            Sent to
          </span>
          <Input
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={e => setDateTo(e.target.value)}
            className="w-[160px]"
          />
        </label>

        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Clear filters
          </Button>
        )}

        <span className="text-xs text-muted-foreground ml-auto">
          Newest first — the log has no other ordering.
        </span>
      </div>

      {invertedRange && (
        <p className="text-xs text-warning">
          The start date is after the end date, so nothing can match.
        </p>
      )}

      <Card className="glass overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Transmitted</th>
                <th className="px-4 py-3 text-left font-medium">Vendor</th>
                {isPos && (
                  <th className="px-4 py-3 text-left font-medium">
                    Reported window
                  </th>
                )}
                <th className="px-4 py-3 text-left font-medium">Batch</th>
                <th className="px-4 py-3 text-left font-medium">Records</th>
                <th className="px-4 py-3 text-left font-medium">Outcome</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                {isPos && (
                  <th className="px-4 py-3 text-left font-medium">Trigger</th>
                )}
                <th className="px-4 py-3 w-24" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                Array.from({length: 6}).map((_, i) => (
                  <tr key={i}>
                    <td className="px-4 py-3" colSpan={columnCount}>
                      <Skeleton className="h-6 w-full" />
                    </td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="px-4 py-16 text-center text-muted-foreground"
                  >
                    {/* A failed request must not read as an empty log —
                        "nothing was sent" is a claim about the data. */}
                    {isError
                      ? 'Could not load transmissions. Check the connection and try again.'
                      : hasFilters
                        ? 'No transmissions match these filters.'
                        : `No ${isPos ? 'POS' : 'inventory'} transmissions recorded for ${provider.label} yet.`}
                  </td>
                </tr>
              ) : (
                rows.map(row => (
                  <tr
                    key={row.id}
                    className="hover:bg-muted/40 transition-colors"
                  >
                    <td className="px-4 py-3 text-muted-foreground tabular-nums whitespace-nowrap">
                      {formatDateTime(row.modified)}
                    </td>
                    <td className="px-4 py-3 font-medium tabular-nums">
                      {row.vendorId}
                    </td>
                    {isPos && (
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                        {formatWindow(row)}
                      </td>
                    )}
                    <td
                      className="px-4 py-3 tabular-nums text-muted-foreground"
                      // Every batch of one run shares its run timestamp — the
                      // only thing that ties sibling rows together.
                      title={
                        row.runTimestamp
                          ? `Run ${formatDateTime(row.runTimestamp)}`
                          : undefined
                      }
                    >
                      {formatBatch(row)}
                    </td>
                    <td className="px-4 py-3 tabular-nums">
                      {row.recordCount == null
                        ? '—'
                        : formatNumber(row.recordCount)}
                    </td>
                    <td className="px-4 py-3">
                      <OutcomePill outcome={row.outcome} />
                      {row.errorMessage && (
                        <p
                          className="mt-1 max-w-[280px] truncate text-xs text-destructive"
                          title={row.errorMessage}
                        >
                          {row.errorMessage}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {/* Raw, free-text and never branched on — shown because
                          it's the value support will see in the table. */}
                      <Badge
                        variant="outline"
                        className="font-mono text-[11px]"
                      >
                        {row.status}
                      </Badge>
                    </td>
                    {isPos && (
                      <td className="px-4 py-3 capitalize text-muted-foreground">
                        {row.trigger ?? '—'}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end">
                        {resendable && (
                          <ResendRowButton
                            row={row}
                            kind={kind}
                            onSelect={setResendRange}
                          />
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={
                            !row.hasDocument || downloadingId === row.id
                          }
                          onClick={() => requestDownload(row)}
                          aria-label={
                            row.hasDocument
                              ? 'Download transmitted payload'
                              : 'No payload archived for this transmission'
                          }
                          title={
                            row.hasDocument
                              ? 'Download transmitted payload'
                              : 'No payload archived for this transmission'
                          }
                        >
                          {downloadingId === row.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {data && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 text-xs text-muted-foreground border-t">
            <span>
              Page {active.index + 1} · showing {rows.length} transmission
              {rows.length === 1 ? '' : 's'}
              {/* The outcome filter is applied after a page is read, so a page
                  can be short while more rows remain. Say so rather than let a
                  three-row page read as the end of the log. */}
              {outcome !== 'all' && data.hasMore
                ? ' · filtered pages can be short, keep paging'
                : ''}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={active.index === 0 || isLoading}
                onClick={() =>
                  setPaging({...active, index: Math.max(0, active.index - 1)})
                }
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!data.hasMore || isLoading}
                onClick={() => {
                  if (!data.nextCursor) return;
                  const cursors = active.cursors.slice(0, active.index + 1);
                  cursors.push(data.nextCursor);
                  setPaging({signature, cursors, index: active.index + 1});
                }}
              >
                Next
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {resendable && (
        <ResendTransmissionDialog
          kind={kind}
          integration={resendable}
          providerLabel={provider.label}
          open={resendRange !== null}
          onOpenChange={next => {
            if (!next) setResendRange(null);
          }}
          initialRange={resendRange ?? undefined}
        />
      )}
    </div>
  );
}
